import React, { useEffect, useState, useCallback } from 'react';
import { Download, X, Share as ShareIcon, Bell, Check } from 'lucide-react';

const DISMISS_KEY = 'pbk_install_dismissed';
const NOTIF_ASK_KEY = 'pbk_notif_prompt_shown';

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isMobile() {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * InstallPrompt: shows a dismissible banner nudging users to install the PWA.
 * - Hidden if already installed (standalone), if user dismissed, or on desktop.
 * - Android: uses beforeinstallprompt native flow.
 * - iOS: shows an inline instructional popover (Safari has no programmatic install).
 */
export function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (!isMobile()) return;
    if (localStorage.getItem(DISMISS_KEY) === '1') return;
    setShow(true);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const onInstalled = () => {
      setShow(false);
      localStorage.setItem(DISMISS_KEY, '1');
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  };

  const onClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        localStorage.setItem(DISMISS_KEY, '1');
        setShow(false);
      }
      setDeferredPrompt(null);
      return;
    }
    // iOS or Android without native prompt → show help
    setShowIOSHelp(true);
  };

  if (!show) return null;

  return (
    <div
      className="bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 rounded-xl p-4"
      data-testid="install-prompt-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/25 flex items-center justify-center flex-shrink-0">
          <Download className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-foreground mb-1">Install Photog Bizkit on your phone</h3>
          <p className="text-sm text-text-secondary mb-3">
            The full office-in-your-pocket experience — opens like a native app.
          </p>
          <button
            onClick={onClick}
            className="inline-flex items-center gap-1.5 bg-primary text-background px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            data-testid="install-prompt-cta"
          >
            <Download className="w-4 h-4" /> Add to Home Screen
          </button>
        </div>
        <button
          onClick={dismiss}
          className="text-text-muted hover:text-foreground p-1 rounded-lg transition-colors"
          aria-label="Dismiss install prompt"
          data-testid="install-prompt-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {showIOSHelp && (
        <div
          className="mt-4 bg-background/40 border border-border rounded-lg p-3 text-sm"
          data-testid="install-ios-help"
        >
          {isIOS() ? (
            <div className="flex items-start gap-2 text-text-secondary">
              <ShareIcon className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
              <span>
                Tap the <strong className="text-foreground">Share</strong> button (□↑) at the bottom of Safari,
                then tap <strong className="text-foreground">"Add to Home Screen"</strong>.
              </span>
            </div>
          ) : (
            <div className="text-text-secondary">
              Open the browser menu (⋮) and choose <strong className="text-foreground">"Add to Home Screen"</strong> or <strong className="text-foreground">"Install App"</strong>.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * NotificationOptIn: after the app is installed (standalone mode), ask user once
 * to enable notifications. Not fired automatically outside of install.
 */
export function NotificationOptIn() {
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState(null);

  const canRun = useCallback(() => {
    return (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'default' &&
      isStandalone() &&
      localStorage.getItem(NOTIF_ASK_KEY) !== '1'
    );
  }, []);

  useEffect(() => {
    if (canRun()) setShow(true);
  }, [canRun]);

  const later = () => {
    localStorage.setItem(NOTIF_ASK_KEY, '1');
    setShow(false);
  };

  const enable = async () => {
    try {
      const result = await Notification.requestPermission();
      setStatus(result);
      localStorage.setItem(NOTIF_ASK_KEY, '1');
      if (result === 'granted') {
        try {
          new Notification('Photog Bizkit', {
            body: "You'll get instant alerts when clients book you.",
            icon: '/icons/icon-192.png',
          });
        } catch (e) { /* ignore */ }
      }
      setTimeout(() => setShow(false), 1200);
    } catch (e) {
      later();
    }
  };

  if (!show) return null;

  return (
    <div
      className="bg-surface border border-border rounded-xl p-4"
      data-testid="notification-optin-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-foreground mb-1">Turn on notifications</h3>
          <p className="text-sm text-text-secondary mb-3">
            Get instant alerts when clients book you or send messages.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={enable}
              className="inline-flex items-center gap-1.5 bg-primary text-background px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              data-testid="notif-enable"
            >
              {status === 'granted' ? <><Check className="w-4 h-4" /> Enabled</> : <><Bell className="w-4 h-4" /> Enable notifications</>}
            </button>
            <button
              onClick={later}
              className="px-3 py-1.5 bg-surface-raised border border-border rounded-lg text-sm text-text-secondary hover:text-foreground transition-colors"
              data-testid="notif-later"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
